import { useEffect, useMemo, useState } from "react";
import {
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSearchSelect,
  FormSection,
} from "@NP-Webber/crm-kit";
import {
  formIntakeApi,
  type FormIntakeCreatePayload,
  type FormIntakeDto,
  type FormIntakeFieldMap,
} from "../../api/formIntakeApi";
import {
  financeStructureApi,
  type FinanceCategory,
} from "../../api/financeStructureApi";
import { QuietButton } from "../../ui/QuietButton";
import { toClientApiError } from "../../api/httpClient";

const FIELD_TARGETS: Array<{
  key: keyof FormIntakeFieldMap;
  label: string;
  requiredHint?: string;
}> = [
  { key: "phone", label: "טלפון", requiredHint: "חובה טלפון או אימייל" },
  { key: "email", label: "אימייל", requiredHint: "חובה טלפון או אימייל" },
  { key: "amount", label: "סכום" },
  { key: "paymentArrangement", label: "אופן תשלום / הסדר תשלום" },
  { key: "title", label: "כותרת מנוי" },
  { key: "notes", label: "הערות" },
  { key: "externalSubmissionId", label: "מזהה שליחה חיצוני (idempotency)" },
];

function collectKeys(value: unknown, prefix = "", into: Set<string>) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${k}` : k;
    into.add(k);
    into.add(path);
    if (v && typeof v === "object" && !Array.isArray(v)) {
      collectKeys(v, path, into);
    }
  }
}

function parseSampleJson(raw: string): {
  ok: true;
  value: Record<string, unknown>;
} | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: false, error: "יש להדביק דוגמת JSON" };
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ok: false, error: "הדוגמה חייבת להיות אובייקט JSON" };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch {
    return { ok: false, error: "JSON לא תקין" };
  }
}

export type FormIntakeWizardDialogProps = {
  open: boolean;
  initial?: FormIntakeDto | null;
  onClose: () => void;
  onSaved: (item: FormIntakeDto) => void;
};

export function FormIntakeWizardDialog({
  open,
  initial,
  onClose,
  onSaved,
}: FormIntakeWizardDialogProps) {
  const isEdit = Boolean(initial);
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [externalFormId, setExternalFormId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState<FinanceCategory[]>([]);
  const [sampleText, setSampleText] = useState("{\n  \"formId\": \"\",\n  \"submissionId\": \"\",\n  \"data\": {\n    \"phone\": \"0501234567\",\n    \"email\": \"example@mail.com\",\n    \"amount\": 100,\n    \"payment\": \"אשראי\"\n  }\n}");
  const [fieldMap, setFieldMap] = useState<FormIntakeFieldMap>({});
  const [autoDetected, setAutoDetected] = useState<
    Partial<Record<keyof FormIntakeFieldMap, boolean>>
  >({});
  const [suggesting, setSuggesting] = useState(false);
  const [suggestInfo, setSuggestInfo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<FormIntakeDto | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setCreated(null);
    setSaving(false);
    setSuggesting(false);
    setSuggestInfo(null);
    setAutoDetected({});
    void financeStructureApi.listCategories().then(setCategories).catch(() => setCategories([]));
    if (initial) {
      setStep(1);
      setName(initial.name);
      setExternalFormId(initial.externalFormId);
      setCategoryId(initial.categoryId);
      setFieldMap(initial.fieldMap ?? {});
      setSampleText(JSON.stringify(initial.samplePayload ?? {}, null, 2));
    } else {
      setStep(1);
      setName("");
      setExternalFormId("");
      setCategoryId("");
      setFieldMap({});
    }
  }, [open, initial]);

  const categoryOptions = useMemo(
    () =>
      categories.map((c) => ({
        key: c.id,
        labelHe: c.displayName ?? c.name,
        defaultAmount: c.defaultSubscriptionAmountAgorot,
      })),
    [categories],
  );

  const selectedCategory =
    categoryOptions.find((o) => o.key === categoryId) ?? null;

  const sampleKeys = useMemo(() => {
    const parsed = parseSampleJson(sampleText);
    if (!parsed.ok) return [] as string[];
    const keys = new Set<string>();
    collectKeys(parsed.value, "", keys);
    return Array.from(keys).sort();
  }, [sampleText]);

  const keyOptions = useMemo(
    () => sampleKeys.map((k) => ({ key: k, labelHe: k })),
    [sampleKeys],
  );

  const canStep1 = name.trim() && externalFormId.trim() && categoryId;
  const canStep2 = parseSampleJson(sampleText).ok;
  const canStep3 = Boolean(fieldMap.phone || fieldMap.email);

  const runSuggestMapping = async (payload: Record<string, unknown>) => {
    setSuggesting(true);
    setSuggestInfo(null);
    setError(null);
    try {
      const result = await formIntakeApi.suggestMapping(payload);
      setFieldMap(result.fieldMap);
      const detected: Partial<Record<keyof FormIntakeFieldMap, boolean>> = {};
      for (const key of result.autoDetectedTargets) {
        detected[key] = true;
      }
      setAutoDetected(detected);
      const mappedCount = Object.keys(result.fieldMap).length;
      setSuggestInfo(
        result.aiEnhanced
          ? `זוהו ${mappedCount} שדות (כולל סיוע AI). אפשר לערוך ידנית.`
          : mappedCount > 0
            ? `זוהו ${mappedCount} שדות אוטומטית. אפשר לערוך ידנית.`
            : "לא זוהו שדות אוטומטית — בחרו מיפוי ידני.",
      );
    } catch (e) {
      setError(toClientApiError(e).message);
    } finally {
      setSuggesting(false);
    }
  };

  const buildPayload = (): FormIntakeCreatePayload | null => {
    const parsed = parseSampleJson(sampleText);
    if (!parsed.ok) {
      setError(parsed.error);
      return null;
    }
    if (!fieldMap.phone && !fieldMap.email) {
      setError("יש למפות לפחות טלפון או אימייל");
      return null;
    }
    const category = categories.find((c) => c.id === categoryId);
    if (!fieldMap.amount && !category?.defaultSubscriptionAmountAgorot) {
      setError("יש למפות סכום או להגדיר ברירת מחדל למנוי בקטגוריה");
      return null;
    }
    return {
      name: name.trim(),
      externalFormId: externalFormId.trim(),
      categoryId,
      fieldMap,
      // External forms send amounts in shekels; server stores agorot.
      amountUnit: "ils",
      samplePayload: parsed.value,
      isActive: true,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    setError(null);
    try {
      const item = initial
        ? await formIntakeApi.update(initial.id, payload)
        : await formIntakeApi.create(payload);
      setCreated(item);
      setStep(4);
      onSaved(item);
    } catch (e) {
      setError(toClientApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  const curlExample = useMemo(() => {
    const url = created?.webhookUrl ?? "https://<host>/api/v1/webhooks/form-intake/<token>";
    const body = sampleText.trim() || "{}";
    return `curl -X POST "${url}" \\\n  -H "Content-Type: application/json" \\\n  -d '${body.replace(/'/g, "'\\''")}'`;
  }, [created, sampleText]);

  if (!open) return null;

  const saveLabel =
    step < 3 ? "המשך" : step === 3 ? (isEdit ? "שמירה" : "יצירת קליטה") : "סיום";

  return (
    <FormDialog
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      size="lg"
    >
      <FormCard
        title={isEdit ? "עריכת קליטת טפסים" : "אשף קליטת טפסים חיצוניים"}
        error={error}
        onSave={() => {
          if (step === 1) {
            if (!canStep1) {
              setError("יש למלא שם, מזהה טופס וקטגוריה");
              return;
            }
            setError(null);
            setStep(2);
            return;
          }
          if (step === 2) {
            const parsed = parseSampleJson(sampleText);
            if (!parsed.ok) {
              setError(parsed.error);
              return;
            }
            setError(null);
            setStep(3);
            void runSuggestMapping(parsed.value);
            return;
          }
          if (step === 3) {
            void handleSave();
            return;
          }
          onClose();
        }}
        onCancel={() => {
          if (step === 4) {
            onClose();
            return;
          }
          if (step > 1 && step < 4 && !saving) {
            setStep((s) => s - 1);
            setError(null);
            return;
          }
          if (!saving) onClose();
        }}
        saveLabel={saveLabel}
        cancelLabel={step === 4 ? "סגירה" : step > 1 && step < 4 ? "הקודם" : "סגירה"}
        saving={saving}
        saveDisabled={
          saving ||
          suggesting ||
          (step === 1 && !canStep1) ||
          (step === 2 && !canStep2) ||
          (step === 3 && !canStep3)
        }
      >
        <FormSection>
          <ol className="import-wizard-steps" aria-label="שלבי אשף קליטה">
            <li className={step === 1 ? "import-wizard-steps__active" : undefined}>
              1. פרטים וקטגוריה
            </li>
            <li className={step === 2 ? "import-wizard-steps__active" : undefined}>
              2. דוגמת JSON
            </li>
            <li className={step === 3 ? "import-wizard-steps__active" : undefined}>
              3. מיפוי שדות
            </li>
            <li className={step === 4 ? "import-wizard-steps__active" : undefined}>
              4. הנחיית שליחה
            </li>
          </ol>

          {step === 1 ? (
            <FormGrid cols={2}>
              <FormField
                label="שם תצוגה"
                required
                value={name}
                onChange={setName}
                disabled={saving}
              />
              <FormField
                label="מזהה טופס חיצוני"
                required
                value={externalFormId}
                onChange={setExternalFormId}
                disabled={saving}
                hint="המזהה כפי שנשלח ממערכת הטפסים (formId)"
              />
              <FormSearchSelect
                label="קטגוריה (מנוי)"
                required
                options={categoryOptions}
                value={selectedCategory}
                onChange={(option) => {
                  const selected = option as { key?: string } | null;
                  setCategoryId(selected?.key ?? "");
                }}
                getOptionLabel={(option) =>
                  String((option as { labelHe?: string }).labelHe ?? "")
                }
                placeholder="חיפוש קטגוריה"
                noOptionsText="אין קטגוריות"
              />
            </FormGrid>
          ) : null}

          {step === 2 ? (
            <FormGrid cols={1}>
              <FormAlert severity="info">
                הדביקו דוגמת JSON אמיתית מהמערכת החיצונית. המפתחות ישמשו למיפוי בשלב הבא.
              </FormAlert>
              <label className="file-import-picker">
                <span className="file-import-picker__label">דוגמת payload</span>
                <textarea
                  className="form-intake-sample-textarea"
                  rows={14}
                  value={sampleText}
                  onChange={(e) => setSampleText(e.target.value)}
                  disabled={saving}
                  dir="ltr"
                  style={{
                    width: "100%",
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 13,
                    padding: 8,
                    boxSizing: "border-box",
                  }}
                />
              </label>
            </FormGrid>
          ) : null}

          {step === 3 ? (
            <FormGrid cols={2}>
              <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <FormAlert severity="info">
                  בחרו לכל יעד את מפתח השדה מהדוגמה. חובה: טלפון או אימייל לזיהוי קופה
                  קיימת. אפשר להריץ זיהוי אוטומטי ולתקן ידנית.
                </FormAlert>
                <QuietButton
                  tone="accent"
                  disabled={suggesting || saving}
                  loading={suggesting}
                  onClick={() => {
                    const parsed = parseSampleJson(sampleText);
                    if (!parsed.ok) {
                      setError(parsed.error);
                      return;
                    }
                    void runSuggestMapping(parsed.value);
                  }}
                >
                  {Object.keys(fieldMap).length > 0 ? "זהה מחדש" : "זיהוי אוטומטי"}
                </QuietButton>
              </div>
              {suggestInfo ? (
                <div style={{ gridColumn: "1 / -1" }}>
                  <FormAlert severity="success">{suggestInfo}</FormAlert>
                </div>
              ) : null}
              {FIELD_TARGETS.map((target) => {
                const currentKey = fieldMap[target.key];
                const value =
                  keyOptions.find((o) => o.key === currentKey) ??
                  (currentKey ? { key: currentKey, labelHe: currentKey } : null);
                const autoHint = autoDetected[target.key]
                  ? "זוהה אוטומטית — ניתן לשנות"
                  : target.requiredHint;
                return (
                  <FormSearchSelect
                    key={target.key}
                    label={target.label}
                    hint={autoHint}
                    options={[{ key: "", labelHe: "— ללא —" }, ...keyOptions]}
                    value={value}
                    onChange={(option) => {
                      const selected = option as { key?: string } | null;
                      const key = selected?.key?.trim() || "";
                      setFieldMap((prev) => {
                        const next = { ...prev };
                        if (!key) {
                          delete next[target.key];
                        } else {
                          next[target.key] = key;
                        }
                        return next;
                      });
                      setAutoDetected((prev) => {
                        const next = { ...prev };
                        delete next[target.key];
                        return next;
                      });
                    }}
                    getOptionLabel={(option) =>
                      String((option as { labelHe?: string }).labelHe ?? "")
                    }
                    placeholder="בחירת מפתח"
                    noOptionsText="אין מפתחות — חזרו לדוגמה"
                    disabled={suggesting || saving}
                  />
                );
              })}
            </FormGrid>
          ) : null}

          {step === 4 && created ? (
            <FormGrid cols={1}>
              <FormAlert severity="success">
                הקליטה פעילה. שלחו POST לכתובת הבאה ממערכת הטפסים החיצונית.
              </FormAlert>
              <FormField
                label="כתובת Webhook"
                value={created.webhookUrl}
                onChange={() => undefined}
                disabled
              />
              <label className="file-import-picker">
                <span className="file-import-picker__label">דוגמת curl (להעתקה)</span>
                <textarea
                  readOnly
                  rows={8}
                  value={curlExample}
                  dir="ltr"
                  style={{
                    width: "100%",
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 12,
                    padding: 8,
                    boxSizing: "border-box",
                  }}
                />
              </label>
            </FormGrid>
          ) : null}
        </FormSection>
      </FormCard>
    </FormDialog>
  );
}
